'use client'

import { useState, useMemo } from 'react'
import { NewsFavicon } from '@/components/NewsFavicon'
import { ReliabilityDots } from '@/components/ui/SourceMeta'
import Tooltip from '@/components/tooltip'
import { RelativeTime } from '@/utils/date'
import { RiLock2Line, RiCloseLine } from 'react-icons/ri'
import Hr from '@/components/ui/hr'
import { useTopicContext } from './TopicContext'
import { getBiasCategory, getBiasLabel, getReliabilityInfo } from '@/utils/bias'

// Mini chart component for the sidebar
function OutletChart({ outlets, selectedOutlet, onOutletClick }) {
  const [hoveredOutlet, setHoveredOutlet] = useState(null)

  if (!outlets || outlets.length === 0) return null

  // Chart dimensions
  const biasMin = -30, biasMax = 30
  const reliabilityMin = 0, reliabilityMax = 64

  const biasToPercent = (bias) => ((bias - biasMin) / (biasMax - biasMin)) * 100
  const reliabilityToPercent = (reliability) => 100 - ((reliability - reliabilityMin) / (reliabilityMax - reliabilityMin)) * 100

  // Reliability thresholds for gridlines and labels
  const reliabilityThresholds = [
    { value: 40, label: 'Reliable' },
    { value: 32, label: 'Mostly' },
    { value: 24, label: 'Mixed' },
  ]

  return (
    <div className="w-full mb-4">
      <div className="relative w-full aspect-[3/2] bg-background border border-border rounded-sm overflow-hidden">
        {/* Chart area */}
        <div className="absolute inset-2">
          {/* Background gradient zones */}
          <div className="absolute inset-0 flex rounded-sm overflow-hidden">
            <div className="h-full bg-blue-500/10" style={{ width: `${biasToPercent(-6)}%` }} />
            <div className="h-full bg-muted/20" style={{ width: `${biasToPercent(6) - biasToPercent(-6)}%` }} />
            <div className="h-full bg-red-500/10" style={{ width: `${100 - biasToPercent(6)}%` }} />
          </div>

          {/* Vertical divider lines (bias) */}
          <div className="absolute top-0 bottom-0 w-px bg-border/50" style={{ left: `${biasToPercent(-6)}%` }} />
          <div className="absolute top-0 bottom-0 w-px bg-border/50" style={{ left: `${biasToPercent(6)}%` }} />

          {/* Horizontal gridlines (reliability) */}
          {reliabilityThresholds.map(({ value }) => (
            <div
              key={value}
              className="absolute left-0 right-0 h-px bg-border/40"
              style={{ top: `${reliabilityToPercent(value)}%` }}
            />
          ))}

          {/* Y-axis labels positioned within their ranges */}
          {/* Reliable: just above first gridline (37.5%) */}
          <span className="absolute left-1 text-[8px] text-muted-foreground" style={{ top: '30%' }}>
            Reliable
          </span>
          {/* Mostly: centered between first (37.5%) and second (50%) gridlines */}
          <span className="absolute left-1 text-[8px] text-muted-foreground" style={{ top: '40%' }}>
            Mostly
          </span>
          {/* Mixed: centered between second (50%) and third (62.5%) gridlines */}
          <span className="absolute left-1 text-[8px] text-muted-foreground" style={{ top: '52%' }}>
            Mixed
          </span>
          {/* Unreliable: just below last gridline (62.5%) */}
          <span className="absolute left-1 text-[8px] text-muted-foreground" style={{ top: '64%' }}>
            Unreliable
          </span>

          {/* Outlet favicons */}
          {outlets.map((outlet) => {
            const x = biasToPercent(outlet.bias ?? 0)
            const y = reliabilityToPercent(outlet.reliability ?? 32)
            const isHovered = hoveredOutlet === outlet.slug
            const isSelected = selectedOutlet === outlet.slug

            return (
              <button
                key={outlet.slug}
                className="absolute transform -translate-x-1/2 -translate-y-1/2 transition-all duration-150"
                style={{
                  left: `${x}%`,
                  top: `${y}%`,
                  zIndex: isHovered || isSelected ? 50 : 10,
                }}
                onClick={() => onOutletClick(outlet.slug)}
                onMouseEnter={() => setHoveredOutlet(outlet.slug)}
                onMouseLeave={() => setHoveredOutlet(null)}
              >
                <div className={`relative transition-transform duration-150 ${isHovered ? 'scale-125' : 'scale-100'}`}>
                  <NewsFavicon
                    src={`${process.env.NEXT_PUBLIC_SUPABASE_URL}/storage/v1/object/public/public_assets/news-favicons/${outlet.slug}.jpg`}
                    faviconSize="sm"
                    alt={outlet.name}
                    className={`rounded-full border-2 shadow-sm transition-all ${
                      isSelected
                        ? 'border-accent ring-2 ring-accent/50'
                        : 'border-background'
                    }`}
                  />
                  {isHovered && (
                    <div className="absolute left-1/2 -translate-x-1/2 -top-8 bg-foreground text-background text-[10px] px-2 py-1 rounded whitespace-nowrap z-50">
                      {outlet.name}
                    </div>
                  )}
                </div>
              </button>
            )
          })}
        </div>

        {/* X-axis labels */}
        <div className="absolute bottom-0.5 left-0 right-0 flex justify-between px-3 text-[9px] text-muted-foreground">
          <span>Left</span>
          <span>Center</span>
          <span>Right</span>
        </div>
      </div>
    </div>
  )
}

const BIAS_FILTERS = [
  { value: 'left', label: 'L', tooltip: 'Left-leaning outlets', borderColor: 'border-l-blue-500', activeBg: 'bg-blue-50 dark:bg-blue-950' },
  { value: 'center', label: 'C', tooltip: 'Center outlets', borderColor: 'border-l-gray-400', activeBg: 'bg-gray-50 dark:bg-gray-900' },
  { value: 'right', label: 'R', tooltip: 'Right-leaning outlets', borderColor: 'border-l-red-500', activeBg: 'bg-red-50 dark:bg-red-950' },
]

export default function ArticleExplorer({ articles = [] }) {
  const { activeSection, clearFilter, filterBySection, highlightedArticleId, biasFilters, toggleBias, subtopics } = useTopicContext()
  const [sortBy, setSortBy] = useState('recent')
  const [selectedOutlet, setSelectedOutlet] = useState(null)

  // Build section options from subtopics (IDs match what TopicContent passes to SubtopicArticles)
  const sectionOptions = useMemo(() => {
    if (!subtopics || subtopics.length === 0) return []

    const mainStory = subtopics.find(st => st.narrative_index === 0 || st.title === 'Main Story')
    const coverageAngles = subtopics.filter(st => st.narrative_index !== 0 && st.title !== 'Main Story')

    const options = []
    if (mainStory) {
      options.push({ id: 'main-story', name: 'Main Story', articles: mainStory.articles || [] })
    }
    coverageAngles.forEach((angle, index) => {
      options.push({ id: `angle-${index + 1}`, name: angle.title, articles: angle.articles || [] })
    })
    return options
  }, [subtopics])

  const handleSectionChange = (e) => {
    const value = e.target.value
    if (value === 'all') {
      clearFilter()
    } else {
      const section = sectionOptions.find(s => s.id.toString() === value)
      if (section) {
        filterBySection(section.id, section.name, section.articles)
      }
    }
    // Clear outlet filter when changing sections
    setSelectedOutlet(null)
  }

  // Use filtered articles from section if active, otherwise all articles
  const baseArticles = activeSection?.articles || articles

  // Extract unique outlets from articles (filtered by bias when no outlet is selected)
  const uniqueOutlets = useMemo(() => {
    const outletMap = new Map()
    baseArticles.forEach(article => {
      if (article.outlet?.slug && !outletMap.has(article.outlet.slug)) {
        // Only include outlets that match the current bias filter (unless an outlet is selected)
        if (!selectedOutlet) {
          const bias = article.outlet?.bias ?? 0
          if (!biasFilters.includes(getBiasCategory(bias))) return
        }
        outletMap.set(article.outlet.slug, article.outlet)
      }
    })
    return Array.from(outletMap.values())
  }, [baseArticles, biasFilters, selectedOutlet])

  const filteredArticles = useMemo(() => {
    let result = [...baseArticles]

    // Filter by selected outlet
    if (selectedOutlet) {
      result = result.filter(article => article.outlet?.slug === selectedOutlet)
    } else {
      // Filter by bias (only when no outlet is selected)
      result = result.filter(article => {
        const bias = article.outlet?.bias ?? 0
        return biasFilters.includes(getBiasCategory(bias))
      })
    }

    // Sort
    result.sort((a, b) => {
      switch (sortBy) {
        case 'recent':
          return new Date(b.published_date || 0) - new Date(a.published_date || 0)
        case 'reliability':
          return (b.outlet?.reliability ?? 0) - (a.outlet?.reliability ?? 0)
        case 'popularity':
          return (b.outlet?.popularity ?? 0) - (a.outlet?.popularity ?? 0)
        default:
          return 0
      }
    })

    return result
  }, [baseArticles, sortBy, biasFilters, selectedOutlet])

  const handleOutletClick = (outletSlug) => {
    setSelectedOutlet(prev => prev === outletSlug ? null : outletSlug)
  }

  const clearOutletFilter = () => {
    setSelectedOutlet(null)
  }

  return (
    <section id="article-explorer" className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl md:text-2xl font-bold font-serif text-foreground tracking-tight">
          Articles
        </h2>
        <span className="text-sm text-muted-foreground">
          {filteredArticles.length} of {baseArticles.length}
        </span>
      </div>
      <Hr />

      {/* Outlet Chart */}
      <div className="mt-4">
        <OutletChart
          outlets={uniqueOutlets}
          selectedOutlet={selectedOutlet}
          onOutletClick={handleOutletClick}
        />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {/* Section filter */}
        {sectionOptions.length > 0 && (
          <select
            value={activeSection?.id?.toString() || 'all'}
            onChange={handleSectionChange}
            className="text-xs bg-muted border-0 rounded px-2 py-1 text-foreground focus:outline-none focus:ring-1 focus:ring-accent max-w-[120px] truncate"
          >
            <option value="all">All Articles</option>
            {sectionOptions.map(section => (
              <option key={section.id} value={section.id.toString()}>
                {section.name}
              </option>
            ))}
          </select>
        )}

        {/* Sort */}
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="text-xs bg-muted border-0 rounded px-2 py-1 text-foreground focus:outline-none focus:ring-1 focus:ring-accent"
        >
          <option value="recent">Recency</option>
          <option value="reliability">Reliability</option>
          <option value="popularity">Popularity</option>
        </select>

        {/* Bias toggles (hidden when outlet is selected) */}
        {!selectedOutlet && (
          <div className="flex gap-0.5">
            {BIAS_FILTERS.map(filter => {
              const isActive = biasFilters.includes(filter.value)
              return (
                <Tooltip key={filter.value} message={filter.tooltip}>
                  <button
                    onClick={() => toggleBias(filter.value)}
                    className={`w-7 h-6 text-xs font-bold border-l-[3px] rounded-r transition-colors ${
                      isActive
                        ? `${filter.borderColor} ${filter.activeBg} text-foreground`
                        : 'border-l-transparent bg-muted text-muted-foreground'
                    }`}
                  >
                    {filter.label}
                  </button>
                </Tooltip>
              )
            })}
          </div>
        )}

        {/* Selected outlet indicator */}
        {selectedOutlet && (
          <div className="flex items-center gap-1.5 px-2 py-1 bg-accent/10 rounded text-xs">
            <span className="font-medium text-accent">
              {uniqueOutlets.find(o => o.slug === selectedOutlet)?.name}
            </span>
            <button
              onClick={clearOutletFilter}
              className="text-accent hover:text-accent/70"
            >
              <RiCloseLine className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>

      {/* Article list */}
      <div className="flex-1 overflow-y-auto space-y-2 min-h-0 max-h-[300px] lg:max-h-[400px]">
        {filteredArticles.map((article) => {
          const biasLabel = getBiasLabel(article.outlet?.bias ?? 0)
          const reliabilityInfo = getReliabilityInfo(article.outlet?.reliability ?? 0)
          const isHighlighted = highlightedArticleId === article.id
          return (
          <a
            key={article.id}
            id={`article-${article.id}`}
            href={article.url}
            target="_blank"
            rel="noopener noreferrer"
            className={`group block p-2 rounded-sm border border-border hover:bg-muted/30 transition-all ${
              isHighlighted ? 'ring-2 ring-accent bg-accent/10' : ''
            }`}
          >
            <div className="flex items-start gap-2">
              <NewsFavicon
                src={`${process.env.NEXT_PUBLIC_SUPABASE_URL}/storage/v1/object/public/public_assets/news-favicons/${article.outlet?.slug}.jpg`}
                alt={article.outlet?.name}
                faviconSize="xs"
                className="flex-shrink-0 mt-0.5"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground leading-snug line-clamp-2 group-hover:text-accent transition-colors">
                  {article.title}
                </p>
                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                  <span className="text-xs text-muted-foreground truncate">
                    {article.outlet?.name}
                  </span>
                  <span className={`text-xs font-medium ${biasLabel.color}`}>
                    {biasLabel.label}
                  </span>
                  <Tooltip message={reliabilityInfo.description}>
                    <span className="flex items-center gap-1">
                      <ReliabilityDots reliability={article.outlet?.reliability} size="sm" />
                    </span>
                  </Tooltip>
                  {article.outlet?.is_paywalled && (
                    <Tooltip message="This outlet requires a subscription to read full articles.">
                      <span><RiLock2Line className="w-3 h-3 text-muted-foreground" /></span>
                    </Tooltip>
                  )}
                  <span className="text-xs text-muted-foreground ml-auto flex-shrink-0">
                    <RelativeTime date={article.published_date} />
                  </span>
                </div>
              </div>
            </div>
          </a>
          )
        })}

        {filteredArticles.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-4">
            No articles match filters
          </p>
        )}
      </div>
    </section>
  )
}
